import { UNSPECIFIED_LABEL } from '../../../../config/labels';
import type { FormField } from '../../../../types/form';
import type { LastUpdated } from '../../../../types/last-updated';
import type { RoomProps } from '../../../../types/room';
import { Statuses } from '../../../../types/status';
import type { RoomSchedule, SurveyProps } from '../../../../types/survey';
import { getImportsSortedByRoom } from '../../../../shared/importsByRoom';
import type { ImportRowType, SetupRowType } from './bulk-uploader-validation';

type BulkSurveyClient = {
  id: string;
  name: string;
  orgId?: string | null;
  orgName?: string | null;
};

type BulkSurveyLocation = {
  id: string;
  name: string;
};

export type BulkSurveyor = {
  nameFirst: string;
  nameLast: string;
  uid: string;
  email: string;
};

type BuildBulkSurveyPlanInput = {
  surveyId: string;
  client: BulkSurveyClient;
  assetListId: string;
  site: BulkSurveyLocation;
  building: BulkSurveyLocation;
  surveyor: BulkSurveyor;
  setup: SetupRowType;
  formFields: FormField[];
  roomSchedule: RoomSchedule;
  imports: ImportRowType[];
  created: number;
  lastUpdated: LastUpdated;
};

export function buildBulkSurveyPlan({
  surveyId,
  client,
  assetListId,
  site,
  building,
  surveyor,
  setup,
  formFields,
  roomSchedule,
  imports,
  created,
  lastUpdated,
}: BuildBulkSurveyPlanInput) {
  const importedRoomSchedule: RoomSchedule = {
    ...roomSchedule,
    data: roomSchedule.data.map((room) => ({
      ...room,
      notes: room.notes ?? '',
      imported: true,
    })),
  };
  const roomDocuments: RoomProps[] = importedRoomSchedule.data.map((room) => ({
    ...room,
    id: room.id,
    clientId: client.id,
    siteId: site.id,
    siteName: site.name,
    buildingId: building.id,
    buildingName: building.name,
    surveyId,
    surveyName: setup['Survey Name'],
    created,
    status: Statuses.NOT_STARTED,
    surveyorStatus: {},
    deleted: 0,
    assetsNumCreated: 0,
    assetsNumDeleted: 0,
    assetsNumFlagged: 0,
    partialsNumCreated: 0,
    partialsNumDeleted: 0,
    images: [],
    images360: [],
    imported: true,
    lastUpdated,
  }));
  const formOptions = {
    cost: setup['Enable Cost'] === 'YES',
    unit: setup['Enable UoM'] === 'YES',
    uplift: setup['Enable Uplift'] === 'YES',
  };
  const isAssigned = surveyor.uid !== UNSPECIFIED_LABEL;
  const surveyorName = isAssigned
    ? `${surveyor.nameFirst} ${surveyor.nameLast}`
    : 'Not Assigned';
  const surveyorIds = isAssigned ? [surveyor.uid] : [];
  const surveyors = isAssigned
    ? {
        [surveyor.uid]: {
          name: surveyorName,
          status: Statuses.ISSUED,
          lastUpdated,
        },
      }
    : {};
  const survey: SurveyProps = {
    id: surveyId,
    assetListId,
    assetsNumCreated: 0,
    assetsNumDeleted: 0,
    assetsNumImported: 0,
    assetsNumFlagged: 0,
    buildingId: building.id,
    buildingName: building.name,
    clientId: client.id,
    clientName: client.name,
    created,
    deleted: 0,
    duplicatedSurveyId: null,
    estimatedStartDate: setup['Estimated Start Date'],
    estimatedDuration: setup['Estimated Days to Complete'],
    external: setup.External ?? 0,
    floorPlans: [],
    floorPlansVersion: 1,
    formFields,
    formFieldsNum: formFields.length,
    formFieldsVersion: 1,
    formOptions,
    gifa: setup.GIFA ?? 0,
    lastUpdated,
    name: setup['Survey Name'],
    orgId: client.orgId ?? null,
    orgName: client.orgName ?? null,
    partialsNumCreated: 0,
    partialsNumDeleted: 0,
    roomSchedule: importedRoomSchedule,
    roomsNumCompleted: 0,
    roomsNumDeleted: 0,
    roomsNumInProgress: 0,
    roomsNumNoAccess: 0,
    roomsNumNotStarted: importedRoomSchedule.data.length,
    rules: {
      image: {
        requiredForNewAsset: setup['Require Image For New Assets'],
        requiredForImportedAsset: setup['Require Image For Imported Assets'],
        requiredForNewRoom: setup['Require Image For New Rooms'],
        requiredForImportedRoom: setup['Require Image For Imported Rooms'],
      },
    },
    schemaFields: [],
    siteId: site.id,
    siteName: site.name,
    status: isAssigned ? Statuses.ISSUED : Statuses.UNASSIGNED,
    surveyTags: [],
    surveyType: setup['Survey Type'],
    surveyorEmail: isAssigned ? surveyor.email : UNSPECIFIED_LABEL,
    surveyorId: isAssigned ? surveyor.uid : UNSPECIFIED_LABEL,
    surveyorIds,
    surveyors,
    surveyorName,
    synced: 0,
    uploadsLost: 0,
    uploadsNotFound: 0,
    uploadsOnOtherDevice: 0,
    uploadsQueueSize: 0,
  };
  const importsSortedByRoom = getImportsSortedByRoom(
    imports,
    importedRoomSchedule.data,
  );
  const importPayload = imports.length
    ? {
        importsSortedByRoom,
        clientId: client.id,
        surveyId,
        name: survey.name,
        siteId: site.id,
        siteName: site.name,
        buildingId: building.id,
        buildingName: building.name,
        formFields,
        roomSchedule: importedRoomSchedule,
        assetsNumImported: 0,
        assetListId,
      }
    : null;

  return {
    survey,
    roomDocuments,
    formConfig: {
      formFields,
      formOptions,
      lastUpdated,
    },
    floorPlansConfig: {
      floorPlansByFloor: {},
      lastUpdated,
    },
    importsSortedByRoom,
    importPayload,
  };
}
